import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
    buildProviders,
    Coerce,
    KalDate,
    KalDialogConfig,
    KalDialogService,
    KalSnackbarConfig,
    KalSnackbarService
} from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import gql from 'graphql-tag';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { VoucherService } from 'src/app/_core/services/voucher.service';
import { CustomFormControlAccessWithLoaders } from 'src/app/_core/tools/custom-form-control-access';
import {
    SharedAddCommentDialogComponent,
    SharedAddCommentDialogData
} from 'src/app/_shared/dialogs/shared-add-comment-dialog/shared-add-comment-dialog.component';
import { IsStringNullOrEmptyPipe } from 'src/app/_shared/pipes/is-string-null-or-empty.pipe';
import { getISODate } from 'src/app/utils/date.utils';
import { AppFile, createNewFile } from 'src/app/utils/file.utils';
import {
    MutationSaveListePieceJustificativeBenefArgs,
    PieceJustificativeBenefModel,
    StatusDtoEnum,
    TypeStatutPieceJustificativeEnum
} from 'src/graphql/generated-types';

@Component({
    selector: 'app-shared-voucher',
    templateUrl: './shared-voucher.component.html',
    styleUrls: ['./shared-voucher.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedVoucherComponent),
    standalone: false
})
export class SharedVoucherComponent extends CustomFormControlAccessWithLoaders implements OnInit {

    form: FormGroup;

    @Input() @Coerce('boolean') disableRemoteCommentSave = false;
    @Input({transform: coerceBooleanProperty}) displayCommentSection = false;

    constructor(protected readonly cdr: ChangeDetectorRef,
                protected readonly store: Store,
                private readonly fb: FormBuilder,
                private readonly dialogService: KalDialogService,
                private readonly graphqlService: GraphqlService,
                private readonly snackbarService: KalSnackbarService,
                private readonly voucherService: VoucherService) {
        super(cdr);
    }

    private _voucherName: string;

    get voucherName(): string {
        return this._voucherName || 'document';
    }

    get attachment(): PieceJustificativeBenefModel {
        return this.form.value;
    }

    get shouldDisplayAttachmentFile(): boolean {
        if (this.isCommentOnlyContext) {
            return false;
        }
        return (this.attachment.IdPJBenef && !this.attachment.IsDeleted) || !!this.attachment.Fichier?.FileBytes;
    }

    get attachmentComment(): string | null {
        if (['RemarquePubliquePJ', 'RemarquePriveePJ'].every(e => !this.attachment?.[e])) {
            return null;
        }

        let comment = '';

        if (this.attachment?.RemarquePubliquePJ) {
            comment += `Commentaire public : ${this.attachment.RemarquePubliquePJ}<br>`;
        }

        if (this.attachment?.RemarquePriveePJ) {
            comment += `Commentaire privé : ${this.attachment.RemarquePriveePJ}`;
        }

        return comment;
    }

    get hasFilenameWithId(): boolean {
        return !IsStringNullOrEmptyPipe.format(this.attachment.NomFichier) && !!this.attachment.IdPJBenef;
    }

    /**
     * Contexte d'ajout de commentaire sans ajouter de PJ
     * Un ID PJ est quand même créé, mais seul NomFichier permet de savoir si il s'agit d'une vraie PJ ou d'une fausse (pour commentaires uniquement)
     */
    get isCommentOnlyContext(): boolean {
        return IsStringNullOrEmptyPipe.format(this.attachment.NomFichier) && !!this.attachment.IdPJBenef;
    }

    private saveVoucherComments$(voucher: PieceJustificativeBenefModel): Observable<string> {
        return this.graphqlService.mutateOne<'SaveListePieceJustificativeBenef', MutationSaveListePieceJustificativeBenefArgs>({
            mutation: gql`
                mutation SaveListePieceJustificativeBenef(
                    $contexte: ContextInfosInput,
                    $idBenef: Float,
                    $listePieceJustificative: ArrayOfPieceJustificativeBenefModelInput
                ) {
                    SaveListePieceJustificativeBenef(
                        contexte: $contexte
                        idBenef: $idBenef
                        listePieceJustificative: $listePieceJustificative
                    )
                }
            `,
            variables: {
                idBenef: voucher.IdBenef,
                listePieceJustificative: {
                    PieceJustificativeBenefModel: [{
                        ...voucher,
                        ...(this.isCommentOnlyContext && IsStringNullOrEmptyPipe.format(this.attachmentComment) ? {
                            Status: StatusDtoEnum.Deleted,
                            IsDeleted: true
                        } : {
                            Status: StatusDtoEnum.Updated,
                            IsUpdated: true
                        }),
                        StatutPJEnum: voucher.IdPJBenef && voucher.StatutPJEnum ? voucher.StatutPJEnum : TypeStatutPieceJustificativeEnum.Abs
                    }]
                }
            }
        });
    }

    writeValue(value: any): void {
        if (value === null) {
            return;
        }

        this._voucherName = value.LibellePJ;
        this.form.patchValue(value);
    }

    addAttachment(appFile: AppFile): void {
        if (!appFile) {
            return;
        }

        this.form.patchValue({
            NomFichier: appFile.file.name,
            IsEnvoyee: true,
            DateEnvoi: getISODate(new KalDate()),
            IsRecue: true,
            DateReception: getISODate(new KalDate()),
            IsValide: true,
            Fichier: createNewFile(appFile),
            IsUpdated: false,
            IsDeleted: false,
        });
    }

    editAttachment(appFile: AppFile): void {
        const value = this.form.value;

        this.form.patchValue({
            NomFichier: appFile.file.name,
            IsDeleted: !!value.IdPJBenef ? false : value.IsDeleted,
            IsUpdated: true,
            Fichier: createNewFile(appFile)
        });
    }

    deleteAttachment(): void {
        const {IdPJBenef, IsDeleted, IsUpdated} = this.form.value;

        this.form.patchValue({
            IsDeleted: IdPJBenef ? true : IsDeleted,
            IsUpdated: IdPJBenef ? false : IsUpdated,
            Fichier: null,
            IsValide: false,
            IsRecue: false,
            IsEnvoyee: false,
            DateEnvoi: null,
            DateReception: null,
            NomFichier: '',
            CodePJ: null,
            OrdreTypePJ: null,
            RemarquePubliquePJ: null,
            RemarquePriveePJ: null,
            RemarquePublique: null,
            Status: null,
            StatutPJEnum: null
        });
    }

    openAttachment(): void {
        if (!!this.attachment.Fichier?.FileBytes) {
            this.snackbarService.open(new KalSnackbarConfig({title: 'Impossible d\'ouvrir le fichier tant que celui-ci n\'a pas été sauvegardé.'}));
            return;
        }

        this.watch$ = this.voucherService.openVoucherBeneficiary(this.attachment);
    }

    hasVoucherComments(voucher: PieceJustificativeBenefModel): boolean {
        return !!voucher.RemarquePublique || !!voucher.RemarquePubliquePJ || !!voucher.RemarquePriveePJ;
    }

    openVoucherCommentDialog(voucher: PieceJustificativeBenefModel): void {
        const dialogConfig = new KalDialogConfig<SharedAddCommentDialogData>({
            title: ['RemarquePubliquePJ', 'RemarquePriveePJ'].some(e => !!voucher[e])
                ? 'Modifier les commentaires' : 'Ajouter des commentaires',
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                publicComment: voucher.RemarquePubliquePJ,
                privateComment: voucher.RemarquePriveePJ
            }
        });

        this.watch$ = this.dialogService.open(SharedAddCommentDialogComponent, dialogConfig).afterClosed.pipe(
            filter(e => !!e),
            tap(() => this.loading = true),
            switchMap((comments: SharedAddCommentDialogData) => {
                if (this.disableRemoteCommentSave) {
                    return of(null).pipe(map(() => comments));
                }

                return this.saveVoucherComments$({
                    ...voucher,
                    RemarquePubliquePJ: comments.publicComment,
                    RemarquePriveePJ: comments.privateComment,
                    RemarquePublique: comments.publicComment
                }).pipe(map(() => comments));
            }),
            tap((comments: SharedAddCommentDialogData) => {
                this.form.patchValue({
                    RemarquePubliquePJ: comments.publicComment,
                    RemarquePriveePJ: comments.privateComment,
                    RemarquePublique: comments.publicComment
                });

                if (this.isCommentOnlyContext && IsStringNullOrEmptyPipe.format(this.attachmentComment)) {
                    this.deleteAttachment();
                }

                const message = comments.toSnackbarMessage();
                if (!!message) {
                    this.snackbarService.open({title: message});
                }
                this.loading = false;
            }),
            catchError(err => {
                this.loading = false;
                return throwError(() => err);
            })
        );
    }

    toggleReceivedAttachment(isReceived: boolean): void {
        const {IdPJBenef, IsUpdated, DateEnvoi} = this.attachment;

        this.form.patchValue({
            IsUpdated: IdPJBenef ? true : IsUpdated,
            IsRecue: isReceived,
            DateReception: isReceived ? getISODate(new KalDate()) : null,
            IsValide: isReceived,
            IsEnvoyee: true,
            DateEnvoi: DateEnvoi ? DateEnvoi : getISODate(new KalDate())
        });
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            IdBenef: null,
            IdPJBenef: null,
            IdPJDossier: null,
            IdPieceJustificative: null,
            IdRib: null,
            NomFichier: '',
            Fichier: null,
            IsEnvoyee: false,
            DateEnvoi: null,
            IsRecue: false,
            DateReception: null,
            IsValide: false,
            CodeTypePJ: '',
            LibellePJ: '',
            IsPJBenef: false,
            IsDeleted: false,
            IsUpdated: false,
            CodePJ: null,
            IdAyantDroit: null,
            OrdreTypePJ: null,
            RemarquePubliquePJ: null,
            RemarquePriveePJ: null,
            RemarquePublique: null,
            Status: null,
            StatutPJEnum: null
        });

        this.watch$ = this.form.valueChanges.pipe(
            tap(value => this.notifyUpdate(value))
        );
    }

}
